import { ReactNode, useCallback, useState } from 'react';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';

const log = createLogger('SystemStatusHeader');

/**
 * Normalised job counts displayed in the header pills, regardless of
 * which backend produced them.
 *
 * Lightning's `/health` exposes flat ``{active_jobs, pending_jobs, ...}``
 * fields; video's exposes a nested ``counts: {processing, pending,
 * completed, failed}`` object. Both shapes flow through this same
 * component once normalised in `AppShell` — and crucially, the counts
 * follow the active mode so switching apps swaps the numbers.
 */
export interface SystemStatusCounts {
  active: number;
  pending: number;
  completed: number;
  failed: number;
}

interface SystemStatusHeaderProps {
  counts: SystemStatusCounts | null;
  /**
   * Which mini-app's health is being shown. Currently only used to gate
   * lightning-specific affordances (the orphan-jobs cleanup button hits
   * `/api/jobs/cleanup`, an endpoint the video and interior services
   * don't expose).
   */
  mode?: 'lightning' | 'video' | 'interior' | 'ads' | 'api' | 'cost' | 'release-notes';
}

type Tone = 'active' | 'pending' | 'complete' | 'failed';

interface ToneStyle {
  bg: string;
  text: string;
}

// Brand palette picked by the user. Text uses light sky-blue so all four
// pills share a single label/value color (matches the surrounding muted
// blue UI text).
const TONE: Record<Tone, ToneStyle> = {
  active: { bg: 'bg-[#e8f1f5]', text: 'text-[#146c94]' },
  pending: { bg: 'bg-[#E0A890]', text: 'text-sky-100' },
  complete: { bg: 'bg-[#70B77E]', text: 'text-sky-100' },
  failed: { bg: 'bg-[#F0544F]', text: 'text-sky-100' },
};

interface StatCellProps {
  tone: Tone;
  icon: ReactNode;
  value: number;
  label: string;
  /**
   * Tailwind rounding class. Override when butting two cells together
   * to form a segmented pill (e.g. ``rounded-l-lg`` + ``rounded-r-lg``).
   */
  rounded?: string;
  cellClassName?: string;
  trailing?: ReactNode;
}

function StatCell({
  tone,
  icon,
  value,
  label,
  rounded = 'rounded-lg',
  cellClassName,
  trailing,
}: StatCellProps) {
  const t = TONE[tone];
  return (
    <div
      className={`relative flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-2 sm:px-2.5 py-2 sm:py-1 shadow-sm ${rounded} ${t.bg} ${t.text} ${cellClassName ?? ''}`}
    >
      <span className="hidden sm:flex items-center justify-center sm:[&>svg]:w-4 sm:[&>svg]:h-4">
        {icon}
      </span>
      <span className="text-base sm:text-sm font-bold tabular-nums leading-none">{value}</span>
      <span className="text-xs font-semibold leading-none">{label}</span>
      {trailing}
    </div>
  );
}

const ICONS = {
  bolt: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  clock: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  checkCircle: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  xCircle: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export function SystemStatusHeader({ counts, mode = 'lightning' }: SystemStatusHeaderProps) {
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ cleaned: number } | null>(null);

  const cleanupOrphanedJobs = useCallback(async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const response = await apiFetch('/api/jobs/cleanup', { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        log.info(`Cleanup completed: ${data.cleaned} orphaned jobs cleaned`);
        setCleanupResult({ cleaned: data.cleaned });
        setTimeout(() => setCleanupResult(null), 3000);
      } else {
        log.error(`Cleanup failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      log.error('Failed to cleanup orphaned jobs:', error);
    } finally {
      setCleaningUp(false);
    }
  }, []);

  const showCleanupAffordance = mode === 'lightning';

  return (
    <div className="flex flex-col items-stretch gap-2 w-full">
      {counts && (
        <div className="flex flex-row items-center justify-center gap-0 sm:justify-start sm:gap-3 sm:flex-wrap w-full">
          <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wide text-slate-500">
            Current Queue
          </span>
          <div className="grid sm:inline-grid grid-cols-2 items-stretch w-1/2 sm:w-fit">
            <StatCell
              tone="active"
              icon={ICONS.bolt}
              value={counts.active}
              label="Active"
              rounded="rounded-l-lg"
              cellClassName={counts.active > 0 ? 'animate-pulse active-gold-glow' : undefined}
            />
            <StatCell
              tone="pending"
              icon={ICONS.clock}
              value={counts.pending}
              label="Pending"
              rounded="rounded-none sm:rounded-r-lg"
              cellClassName="group"
              trailing={
                showCleanupAffordance &&
                (counts.pending > 0 || counts.active > 0) && (
                  <button
                    onClick={cleanupOrphanedJobs}
                    disabled={cleaningUp}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center shadow-sm ring-2 ring-white"
                    title="Clear stuck jobs"
                  >
                    {cleaningUp ? '·' : '×'}
                  </button>
                )
              }
            />
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 sm:ml-auto w-1/2 sm:w-auto">
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wide text-slate-500">
              Last 24h
            </span>
            <div className="grid sm:inline-grid grid-cols-2 items-stretch w-full sm:w-fit">
              <StatCell
                tone="complete"
                icon={ICONS.checkCircle}
                value={counts.completed}
                label="Complete"
                rounded="rounded-none sm:rounded-l-lg"
              />
              <StatCell
                tone="failed"
                icon={ICONS.xCircle}
                value={counts.failed}
                label="Failed"
                rounded="rounded-r-lg"
              />
            </div>
          </div>
        </div>
      )}

      {cleanupResult && (
        <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-xs flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Cleaned up {cleanupResult.cleaned} orphaned job{cleanupResult.cleaned !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
